import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import Dialog from '../ui/Dialog';
import Field from '../ui/Field';
import CustomerPicker from '../ui/CustomerPicker';
import { api } from '../../services/api';
import EntityPicker from '../ui/EntityPicker';
import { toast } from '../../store/toastStore';
import {
  assignErrorCode, buildAssignBody, candidateKey, suggestedCandidate, type AssignCandidate,
} from './assignCustomerModel';

/**
 * D-213..D-215 (P-43) — "שיוך ללקוח": a request that arrived with no customer gets one.
 * The customer is required; "opened by" is a suggestion by the sender's e-mail, never required.
 * The server decides who may (OTE manager / the chosen customer's account manager) and refuses
 * hours in an approved month, a running timer, and so on; the refusal is shown here in words.
 */
export default function AssignCustomerDialog({ ticketId, ticketTitle, onClose }: {
  ticketId: string;
  ticketTitle: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [orgId, setOrgId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [personKey, setPersonKey] = useState<string | null>(null); // null = not touched yet (the suggestion applies)
  const [touched, setTouched] = useState(false);

  const candidates = useQuery({
    queryKey: ['assign-customer-candidates', ticketId, orgId],
    queryFn: async () => (await api.getAssignCustomerCandidates(ticketId, orgId)).data as AssignCandidate[],
    enabled: !!orgId,
    staleTime: 30_000,
  });
  const list = useMemo(() => candidates.data ?? [], [candidates.data]);
  useEffect(() => { setPersonKey(null); }, [orgId]);
  const chosen = useMemo<AssignCandidate | null>(() => {
    if (personKey === '') return null; // explicitly "none"
    if (personKey) return list.find((c) => candidateKey(c) === personKey) ?? null;
    return suggestedCandidate(list);
  }, [list, personKey]);

  const mutation = useMutation({
    mutationFn: () => api.assignTicketCustomer(ticketId, buildAssignBody(orgId, chosen)),
    onSuccess: () => {
      toast('success', t('ticketDetail.assignCustomer.done', { name: orgName }));
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      onClose();
    },
  });
  const code = mutation.isError ? assignErrorCode(mutation.error) : null;
  const serverText = (mutation.error as { error?: { message?: string } } | null)?.error?.message;
  const errorText = mutation.isError
    ? (code ? t(`ticketDetail.assignCustomer.errors.${code}`) : (serverText || t('ticketDetail.assignCustomer.failed')))
    : null;
  const missing = touched && !orgId;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('ticketDetail.assignCustomer.title')}
      description={<bdi className="font-medium text-text">{ticketTitle}</bdi>}
      size="md"
      dismissible={!mutation.isPending}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={mutation.isPending}
            aria-busy={mutation.isPending || undefined}
            onClick={() => { setTouched(true); if (orgId) mutation.mutate(); }}
          >
            {mutation.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('ticketDetail.assignCustomer.confirm')}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-sm text-surface-700">{t('ticketDetail.assignCustomer.intro')}</p>
        <div>
          <CustomerPicker
            label={t('ticketDetail.assignCustomer.customer')}
            value={orgId}
            required
            invalid={missing}
            clearable={false}
            onChange={(id, c) => { setOrgId(id); setOrgName(c?.name ?? ''); mutation.reset(); }}
          />
          {missing && <p className="error-text mt-1" role="alert">{t('ticketDetail.assignCustomer.customerRequired')}</p>}
        </div>
        {orgId && (
          <Field id="ac-person" label={t('ticketDetail.assignCustomer.person')} optional hint={t('ticketDetail.assignCustomer.personHint')}>
            {/* DQ-449 (D-203/D-207): a name picker with search. */}
            <EntityPicker
              emptyLabel={t('ticketDetail.assignCustomer.personNone')}
              options={list.map((c) => ({
                value: candidateKey(c),
                label: `${c.name}${c.match ? ` · ${t('ticketDetail.assignCustomer.match')}` : ''}${c.kind === 'contact' && !c.user_id ? ` · ${t('ticketDetail.assignCustomer.noLogin')}` : ''}`,
                secondary: c.email || undefined,
                keywords: c.email || undefined,
              }))}
              value={chosen ? candidateKey(chosen) : ''}
              onChange={(v) => setPersonKey(v)}
              disabled={candidates.isLoading}
            />
          </Field>
        )}
        <p className="text-xs text-surface-600">{t('ticketDetail.assignCustomer.effects')}</p>
        {errorText && <p className="error-text" role="alert">{errorText}</p>}
      </div>
    </Dialog>
  );
}
